import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { adminAPI } from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import {
    UsersIcon,
    CheckCircleIcon,
    ClockIcon,
    UserGroupIcon,
    MagnifyingGlassIcon,
    FunnelIcon,
    ArrowPathIcon,
    TrashIcon,
    ShieldCheckIcon,
    UserIcon,
    EnvelopeIcon,
    CalendarIcon,
    ChevronDownIcon,
    XMarkIcon,
    CheckIcon,
    EyeIcon,
    PencilIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow, format } from 'date-fns';
import toast from 'react-hot-toast';

const UsersManagement = () => {
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showFilters, setShowFilters] = useState(false);
    const [sortBy, setSortBy] = useState('newest');
    const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserModal, setShowUserModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);

    const statusOptions = [
        { value: 'all', label: 'All Users', icon: UsersIcon, color: 'blue' },
        { value: 'pending', label: 'Pending Approval', icon: ClockIcon, color: 'yellow' },
        { value: 'approved', label: 'Approved Users', icon: CheckCircleIcon, color: 'green' },
        { value: 'admins', label: 'Administrators', icon: ShieldCheckIcon, color: 'purple' },
        { value: 'deactivated', label: 'Deactivated', icon: TrashIcon, color: 'red' },
    ];

    const sortOptions = [
        { value: 'newest', label: 'Newest First' },
        { value: 'oldest', label: 'Oldest First' },
        { value: 'name', label: 'Name A-Z' },
        { value: 'nameDesc', label: 'Name Z-A' },
        { value: 'email', label: 'Email A-Z' },
    ];

    useEffect(() => {
        loadStats();
    }, []);

    useEffect(() => {
        loadUsers();
    }, [selectedStatus, searchTerm, sortBy]);

    const loadStats = async () => {
        setStatsLoading(true);
        try {
            const response = await adminAPI.getStats();
            debugger
            setStats(response.data);
        } catch (error) {
            toast.error('Failed to load statistics');
        } finally {
            setStatsLoading(false);
        }
    };

    const loadUsers = async () => {
        setLoading(true);
        try {
            const status = selectedStatus === 'all' ? '' : selectedStatus;
            const response = await adminAPI.getUsers(status, searchTerm);
            let sortedUsers = [...response.data];

            // Apply sorting
            switch (sortBy) {
                case 'newest':
                    sortedUsers.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
                    break;
                case 'oldest':
                    sortedUsers.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                    break;
                case 'name':
                    sortedUsers.sort((a, b) => a.username.localeCompare(b.username));
                    break;
                case 'nameDesc':
                    sortedUsers.sort((a, b) => b.username.localeCompare(a.username));
                    break;
                case 'email':
                    sortedUsers.sort((a, b) => a.email.localeCompare(b.email));
                    break;
                default:
                    break;
            }

            setUsers(sortedUsers);
        } catch (error) {
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (userId) => {
        try {
            await adminAPI.approveUser(userId);
            toast.success('User approved successfully');
            loadUsers();
            loadStats();
        } catch (error) {
            toast.error('Failed to approve user');
        }
    };

    const handleReject = async (userId) => {
        if (window.confirm('Are you sure you want to reject this user?')) {
            try {
                await adminAPI.rejectUser(userId);
                toast.success('User rejected');
                loadUsers();
                loadStats();
            } catch (error) {
                toast.error('Failed to reject user');
            }
        }
    };

    const handleDelete = async (userId) => {
        if (window.confirm('Deactivate this user? They will be unable to log in. Their data is retained and the account can be reactivated later.')) {
            try {
                await adminAPI.deleteUser(userId);
                toast.success('User deactivated');
                loadUsers();
                loadStats();
            } catch (error) {
                toast.error('Failed to deactivate user');
            }
        }
    };

    const handleReactivate = async (userId) => {
        try {
            await adminAPI.reactivateUser(userId);
            toast.success('User reactivated');
            loadUsers();
            loadStats();
        } catch (error) {
            toast.error('Failed to reactivate user');
        }
    };

    const handleToggleApproval = async (userId) => {
        try {
            const response = await adminAPI.toggleApproval(userId);
            toast.success(response.data.message);
            loadUsers();
            loadStats();
        } catch (error) {
            toast.error('Failed to update user status');
        }
    };

    const handleUpdateRole = async (userId, newRole) => {
        try {
            await adminAPI.updateUserRole(userId, newRole);
            toast.success('User role updated successfully');
            loadUsers();
            setShowEditModal(false);
        } catch (error) {
            toast.error('Failed to update user role');
        }
    };

    const handleBulkApprove = async () => {
        if (selectedUsers.length === 0) {
            toast.error('No users selected');
            return;
        }

        try {
            await adminAPI.bulkApprove(selectedUsers);
            toast.success(`${selectedUsers.length} users approved`);
            setSelectedUsers([]);
            loadUsers();
            loadStats();
        } catch (error) {
            toast.error('Failed to approve users');
        }
    };

    const handleBulkDelete = async () => {
        if (selectedUsers.length === 0) {
            toast.error('No users selected');
            return;
        }

        if (window.confirm(`Are you sure you want to delete ${selectedUsers.length} users?`)) {
            try {
                await adminAPI.bulkDelete(selectedUsers);
                toast.success(`${selectedUsers.length} users deleted`);
                setSelectedUsers([]);
                loadUsers();
                loadStats();
            } catch (error) {
                toast.error('Failed to delete users');
            }
        }
    };

    const toggleUserSelection = (userId) => {
        setSelectedUsers(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const toggleAllSelection = () => {
        if (selectedUsers.length === users.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(users.map(user => user.id));
        }
    };

    const viewUserDetails = (user) => {
        setSelectedUser(user);
        setShowUserModal(true);
    };

    const editUser = (user) => {
        setEditingUser(user);
        setShowEditModal(true);
    };

    const getStatusBadge = (user) => {
        if (user.role === 'admin') {
            return (
                <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 border border-purple-200">
                    <ShieldCheckIcon className="h-3 w-3 mr-1" />
                    Admin
                </span>
            );
        }
        return user.is_approved ? (
            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 border border-green-200">
                <CheckCircleIcon className="h-3 w-3 mr-1" />
                Approved
            </span>
        ) : (
            <span className="px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-200">
                <ClockIcon className="h-3 w-3 mr-1" />
                Pending
            </span>
        );
    };

    // Stat Cards Component
    const StatCards = () => {
        if (statsLoading) {
            return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="bg-white rounded-xl shadow-md p-4 animate-pulse">
                            <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                            <div className="h-8 bg-gray-300 rounded w-1/3"></div>
                        </div>
                    ))}
                </div>
            );
        }

        const statCards = [
            {
                title: 'Total Users',
                value: stats?.totalUsers || 0,
                icon: UserGroupIcon,
                color: 'bg-blue-500',
                bgColor: 'bg-blue-50',
                textColor: 'text-blue-600',
                // change: '+12%',
            },
            {
                title: 'Approved Users',
                value: stats?.approvedUsers || 0,
                icon: CheckCircleIcon,
                color: 'bg-green-500',
                bgColor: 'bg-green-50',
                textColor: 'text-green-600',
                // change: '+8%',
            },
            {
                title: 'Pending Approval',
                value: stats?.pendingUsers || 0,
                icon: ClockIcon,
                color: 'bg-yellow-500',
                bgColor: 'bg-yellow-50',
                textColor: 'text-yellow-600',
                // change: '-2%',
            },
            {
                title: 'Total Admins',
                value: stats?.totalAdmins || 0,
                icon: ShieldCheckIcon,
                color: 'bg-purple-500',
                bgColor: 'bg-purple-50',
                textColor: 'text-purple-600',
                // change: '+5%',
            },
        ];

        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {statCards.map((stat, index) => (
                    <motion.div
                        key={stat.title}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-all duration-200 border border-gray-100"
                    >
                    <a href='#tabledata'>
                        <div className="flex items-center justify-between mb-0">
                            <div className={`p-3 rounded-xl ${stat.bgColor}`}>
                                <stat.icon className={`h-6 w-6 ${stat.textColor}`} />
                            </div>
                            <div className='text-center'>
                                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                                <h3 className="text-sm font-medium text-gray-500 mb-1">{stat.title}</h3>
                            </div>

                        </div>
                        </a>
                    </motion.div>
                ))}
            </div>
        );
    };

    const ActivityChart = () => {
        if (!stats?.recentActivity?.length) return null;

        // Generate last 7 days data
        const last7Days = Array.from({ length: 7 }, (_, i) => {
            const date = new Date();
            date.setDate(date.getDate() - (6 - i));
            const dateStr = date.toISOString().split('T')[0];

            const existingData = stats.recentActivity.find(
                item => new Date(item.date).toDateString() === date.toDateString()
            );

            return {
                date: dateStr,
                count: existingData?.count || 0,
                originalDate: date
            };
        });

        const maxCount = Math.max(...last7Days.map(item => item.count), 1);
        const chartHeight = 200; // Fixed chart height in pixels

        return (
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl shadow-md p-6 mb-8 border border-gray-100"
            >
                <h2 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                    <CalendarIcon className="h-5 w-5 mr-2 text-primary-600" />
                    User Registrations (Last 7 Days)
                </h2>


                <div className="relative">
                    {/* Y-axis labels - dynamically generated without duplicates */}
                    <div className="absolute -left-2 top-0 h-48 flex flex-col justify-between text-xs text-gray-400">
                        {[...new Set([maxCount, Math.round(maxCount * 0.75), Math.round(maxCount * 0.5), Math.round(maxCount * 0.25), 0])].map((value, idx) => (
                            <span key={idx}>{value}</span>
                        ))}
                    </div>

                    {/* Bars container */}
                    <div className="ml-8 h-48 flex items-end space-x-3">
                        {last7Days.map((item, index) => {
                            // Calculate bar height based on count
                            const barHeight = item.count === 0
                                ? 8
                                : (item.count / maxCount) * 160;

                            return (
                                <div key={index} className="flex-1 flex flex-col items-center group h-full">
                                    <div className="relative w-full h-full flex flex-col justify-end">
                                        <motion.div
                                            initial={{ height: 0 }}
                                            animate={{ height: barHeight }}
                                            transition={{
                                                delay: index * 0.05,
                                                duration: 0.5,
                                                ease: "easeOut"
                                            }}
                                            className={`w-full rounded-t-md transition-all duration-300 cursor-pointer ${item.count === 0
                                                    ? 'bg-gradient-to-t from-gray-200 to-gray-100'
                                                    : 'bg-gradient-to-t from-primary-600 to-primary-400 hover:from-primary-700 hover:to-primary-500'
                                                }`}
                                            style={{
                                                height: barHeight,
                                                minHeight: item.count === 0 ? '8px' : '4px'
                                            }}
                                        >
                                            {item.count > 0 && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: 0.5 + index * 0.05 }}
                                                    className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs font-semibold text-primary-600 bg-white px-1.5 py-0.5 rounded-full shadow-sm whitespace-nowrap"
                                                >
                                                    {item.count}
                                                </motion.div>
                                            )}
                                        </motion.div>

                                        <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs rounded-lg py-2 px-3 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20 shadow-lg pointer-events-none">
                                            <div className="font-semibold">{format(new Date(item.date), 'EEEE, MMMM dd')}</div>
                                            <div className="flex items-center mt-1">
                                                <div className={`w-2 h-2 rounded-full mr-1 ${item.count === 0 ? 'bg-gray-400' : 'bg-primary-400'}`}></div>
                                                <span>{item.count} {item.count === 1 ? 'user' : 'users'} registered</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-2 text-center">
                                        <div className="text-xs font-medium text-gray-600">
                                            {format(new Date(item.date), 'EEE')}
                                        </div>
                                        <div className="text-[10px] text-gray-400">
                                            {format(new Date(item.date), 'dd MMM')}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Horizontal grid lines - matching unique Y-axis values */}
                    <div className="absolute left-8 right-0 top-0 h-48 pointer-events-none">
                        {[...new Set([0, 25, 50, 75, 100].map(p => Math.round(p)))].map((percent, idx) => (
                            <div
                                key={idx}
                                className="absolute w-full border-t border-gray-100"
                                style={{ top: `${percent}%` }}
                            ></div>
                        ))}
                    </div>
                </div>

            </motion.div>
        );
    };
    // Table View
    const TableView = () => (
        <div className="overflow-x-auto" id='tabledata'>
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Email
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Registered
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Login
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {users.map((user, index) => (
                        <motion.tr
                            key={user.id}
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.02 }}
                            className="hover:bg-gray-50 transition-colors group"
                        >
                            
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10">
                                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                                            <span className="text-primary-600 font-medium text-lg">
                                                {user.username[0].toUpperCase()}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="ml-4">
                                        <div className="text-sm font-medium text-gray-900">{user.username}</div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{user.email}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                {user.role === 'admin' ? (
                                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">
                                        Admin
                                    </span>
                                ) : (
                                    <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                                        User
                                    </span>
                                )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                {getStatusBadge(user)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {formatDistanceToNow(new Date(user.created_at), { addSuffix: true })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {user.last_login
                                    ? formatDistanceToNow(new Date(user.last_login), { addSuffix: true })
                                    : 'Never'
                                }
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <button
                                    onClick={() => viewUserDetails(user)}
                                    className="text-blue-600 hover:text-blue-900 mr-3 p-1 hover:bg-blue-50 rounded transition-colors"
                                    title="View Details"
                                >
                                    <EyeIcon className="h-5 w-5" />
                                </button>
                                <button
                                    onClick={() => editUser(user)}
                                    className="text-gray-600 hover:text-gray-900 mr-3 p-1 hover:bg-gray-50 rounded transition-colors"
                                    title="Edit User"
                                >
                                    <PencilIcon className="h-5 w-5" />
                                </button>
                                {user.role !== 'admin' && !user.is_approved && (
                                    <button
                                        onClick={() => handleApprove(user.id)}
                                        className="text-green-600 hover:text-green-900 mr-3 p-1 hover:bg-green-50 rounded transition-colors"
                                        title="Approve"
                                    >
                                        <CheckIcon className="h-5 w-5" />
                                    </button>
                                )}
                                {user.role !== 'admin' && user.is_approved && (
                                    <button
                                        onClick={() => handleToggleApproval(user.id)}
                                        className="text-yellow-600 hover:text-yellow-900 mr-3 p-1 hover:bg-yellow-50 rounded transition-colors"
                                        title="Toggle Approval"
                                    >
                                        <ClockIcon className="h-5 w-5" />
                                    </button>
                                )}
                                {user.id !== JSON.parse(localStorage.getItem('user'))?.id && (
                                    user.deleted_at ? (
                                        <button
                                            onClick={() => handleReactivate(user.id)}
                                            className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded transition-colors"
                                            title="Reactivate User"
                                        >
                                            <ArrowPathIcon className="h-5 w-5" />
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleDelete(user.id)}
                                            className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-colors"
                                            title="Deactivate User"
                                        >
                                            <TrashIcon className="h-5 w-5" />
                                        </button>
                                    )
                                )}
                            </td>
                        </motion.tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    // Grid View
    const GridView = () => (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-6">
            {users.map((user, index) => (
                <motion.div
                    key={user.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: index * 0.03 }}
                    className="bg-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 border border-gray-100 overflow-hidden group"
                >
                    <div className="p-6">
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    checked={selectedUsers.includes(user.id)}
                                    onChange={() => toggleUserSelection(user.id)}
                                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                />
                                <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                                    <span className="text-primary-600 font-bold text-xl">
                                        {user.username[0].toUpperCase()}
                                    </span>
                                </div>
                            </div>
                            {getStatusBadge(user)}
                        </div>

                        <h3 className="font-semibold text-gray-900 text-lg mb-1">{user.username}</h3>
                        <p className="text-sm text-gray-500 mb-3 flex items-center">
                            <EnvelopeIcon className="h-4 w-4 mr-1" />
                            {user.email}
                        </p>

                        <div className="space-y-2 mb-4">
                            <div className="flex items-center text-xs text-gray-500">
                                <CalendarIcon className="h-3 w-3 mr-1" />
                                Joined {formatDistanceToNow(new Date(user.created_at), { addSuffix: true })}
                            </div>
                            {user.last_login && (
                                <div className="flex items-center text-xs text-gray-500">
                                    <ClockIcon className="h-3 w-3 mr-1" />
                                    Last active {formatDistanceToNow(new Date(user.last_login), { addSuffix: true })}
                                </div>
                            )}
                        </div>

                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => viewUserDetails(user)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                    title="View Details"
                                >
                                    <EyeIcon className="h-4 w-4" />
                                </button>
                                <button
                                    onClick={() => editUser(user)}
                                    className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                                    title="Edit User"
                                >
                                    <PencilIcon className="h-4 w-4" />
                                </button>
                            </div>
                            {user.role !== 'admin' && (
                                <button
                                    onClick={() => user.is_approved ? handleToggleApproval(user.id) : handleApprove(user.id)}
                                    className={`p-2 rounded-lg transition-colors ${user.is_approved
                                        ? 'text-yellow-600 hover:bg-yellow-50'
                                        : 'text-green-600 hover:bg-green-50'
                                        }`}
                                    title={user.is_approved ? 'Revoke Approval' : 'Approve User'}
                                >
                                    {user.is_approved ? (
                                        <ClockIcon className="h-4 w-4" />
                                    ) : (
                                        <CheckIcon className="h-4 w-4" />
                                    )}
                                </button>
                            )}
                            {user.id !== JSON.parse(localStorage.getItem('user'))?.id && (
                                user.deleted_at ? (
                                    <button
                                        onClick={() => handleReactivate(user.id)}
                                        className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                        title="Reactivate User"
                                    >
                                        <ArrowPathIcon className="h-4 w-4" />
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => handleDelete(user.id)}
                                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                        title="Deactivate User"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                )
                            )}
                        </div>
                    </div>
                </motion.div>
            ))}
        </div>
    );

    // User Details Modal
    const UserDetailsModal = () => (
        <Transition appear show={showUserModal} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setShowUserModal(false)}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                                <div className="flex justify-between items-center mb-6">
                                    <Dialog.Title className="text-xl font-semibold text-gray-900">
                                        User Details
                                    </Dialog.Title>
                                    <button
                                        onClick={() => setShowUserModal(false)}
                                        className="text-gray-400 hover:text-gray-500"
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>

                                {selectedUser && (
                                    <div className="space-y-6">
                                        <div className="flex items-center space-x-4">
                                            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
                                                <span className="text-primary-600 font-bold text-3xl">
                                                    {selectedUser.username[0].toUpperCase()}
                                                </span>
                                            </div>
                                            <div>
                                                <h3 className="text-2xl font-bold text-gray-900">{selectedUser.username}</h3>
                                                <p className="text-gray-500">{selectedUser.email}</p>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                <p className="text-sm text-gray-500 mb-1">Role</p>
                                                <p className="font-medium capitalize">{selectedUser.role}</p>
                                            </div>
                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                <p className="text-sm text-gray-500 mb-1">Status</p>
                                                <p className="font-medium">{selectedUser.is_approved ? 'Approved' : 'Pending'}</p>
                                            </div>
                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                <p className="text-sm text-gray-500 mb-1">Registered</p>
                                                <p className="font-medium">
                                                    {format(new Date(selectedUser.created_at), 'PPP')}
                                                </p>
                                            </div>
                                            <div className="bg-gray-50 p-4 rounded-lg">
                                                <p className="text-sm text-gray-500 mb-1">Last Login</p>
                                                <p className="font-medium">
                                                    {selectedUser.last_login
                                                        ? format(new Date(selectedUser.last_login), 'PPP')
                                                        : 'Never'
                                                    }
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex justify-end space-x-3">
                                            <button
                                                onClick={() => setShowUserModal(false)}
                                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                                            >
                                                Close
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowUserModal(false);
                                                    editUser(selectedUser);
                                                }}
                                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                                            >
                                                Edit User
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );

    // Edit User Modal
    const EditUserModal = () => (
        <Transition appear show={showEditModal} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={() => setShowEditModal(false)}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                                <div className="flex justify-between items-center mb-6">
                                    <Dialog.Title className="text-xl font-semibold text-gray-900">
                                        Edit User
                                    </Dialog.Title>
                                    <button
                                        onClick={() => setShowEditModal(false)}
                                        className="text-gray-400 hover:text-gray-500"
                                    >
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>

                                {editingUser && (
                                    <form onSubmit={(e) => {
                                        e.preventDefault();
                                        const formData = new FormData(e.target);
                                        const newRole = formData.get('role');
                                        handleUpdateRole(editingUser.id, newRole);
                                    }}>
                                        <div className="space-y-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Username
                                                </label>
                                                <input
                                                    type="text"
                                                    value={editingUser.username}
                                                    disabled
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Email
                                                </label>
                                                <input
                                                    type="email"
                                                    value={editingUser.email}
                                                    disabled
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                                    Role
                                                </label>
                                                <select
                                                    name="role"
                                                    defaultValue={editingUser.role}
                                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                >
                                                    <option value="user">User</option>
                                                    <option value="admin">Admin</option>
                                                </select>
                                            </div>

                                            {editingUser.role !== 'admin' && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                                        Approval Status
                                                    </label>
                                                    <div className="flex items-center space-x-4">
                                                        <label className="flex items-center">
                                                            <input
                                                                type="radio"
                                                                name="approval"
                                                                value="approved"
                                                                defaultChecked={editingUser.is_approved}
                                                                className="mr-2"
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        handleToggleApproval(editingUser.id);
                                                                    }
                                                                }}
                                                            />
                                                            Approved
                                                        </label>
                                                        <label className="flex items-center">
                                                            <input
                                                                type="radio"
                                                                name="approval"
                                                                value="pending"
                                                                defaultChecked={!editingUser.is_approved}
                                                                className="mr-2"
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        handleToggleApproval(editingUser.id);
                                                                    }
                                                                }}
                                                            />
                                                            Pending
                                                        </label>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-6 flex justify-end space-x-3">
                                            <button
                                                type="button"
                                                onClick={() => setShowEditModal(false)}
                                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                                            >
                                                Save Changes
                                            </button>
                                        </div>
                                    </form>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-4 lg:px-8 py-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-2"
                >
                    <h1 className="text-2xl font-bold text-gray-900 mb-1">Users Management</h1>
                    <p className="text-gray-600">Manage all users, approvals, and permissions</p>
                </motion.div>

                {/* Statistics Cards */}
                <StatCards />

                {/* Activity Chart */}
                <ActivityChart />

                {/* Filters and Controls */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-white rounded-xl shadow-md p-4 mb-6 border border-gray-100"
                >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Search */}
                        <div className="relative flex-1">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search users by name or email..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                        </div>

                        {/* Filters Toggle */}
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            <FunnelIcon className="h-5 w-5 mr-2 text-gray-500" />
                            Filters
                            <ChevronDownIcon className={`h-4 w-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                        </button>

                        {/* View Toggle */}
                        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
                            <button
                                onClick={() => setViewMode('table')}
                                className={`px-4 py-2 text-sm font-medium transition-colors ${viewMode === 'table'
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                Table
                            </button>
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`px-4 py-2 text-sm font-medium transition-colors ${viewMode === 'grid'
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-50'
                                    }`}
                            >
                                Grid
                            </button>
                        </div>
                    </div>

                    {/* Expanded Filters */}
                    <AnimatePresence>
                        {showFilters && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.2 }}
                                className="overflow-hidden"
                            >
                                <div className="pt-6 mt-6 border-t border-gray-200">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* Status Filter */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                User Status
                                            </label>
                                            <div className="flex flex-wrap gap-2">
                                                {statusOptions.map((option) => (
                                                    <button
                                                        key={option.value}
                                                        onClick={() => setSelectedStatus(option.value)}
                                                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center ${selectedStatus === option.value
                                                            ? `bg-${option.color}-100 text-${option.color}-700 border-2 border-${option.color}-300`
                                                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-transparent'
                                                            }`}
                                                    >
                                                        <option.icon className={`h-4 w-4 mr-1 ${selectedStatus === option.value ? `text-${option.color}-500` : 'text-gray-500'
                                                            }`} />
                                                        {option.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Sort By */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Sort By
                                            </label>
                                            <select
                                                value={sortBy}
                                                onChange={(e) => setSortBy(e.target.value)}
                                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                            >
                                                {sortOptions.map(option => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Quick Actions */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Quick Actions
                                            </label>
                                            <div className="flex space-x-2">
                                                <button
                                                    onClick={handleBulkApprove}
                                                    disabled={selectedUsers.length === 0}
                                                    className="flex-1 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                >
                                                    Approve Selected ({selectedUsers.length})
                                                </button>
                                                <button
                                                    onClick={handleBulkDelete}
                                                    disabled={selectedUsers.length === 0}
                                                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                >
                                                    Delete Selected
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>

                {/* Users List */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100"
                >
                    {loading ? (
                        <div className="p-12 text-center">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-4"></div>
                            <p className="text-gray-500">Loading users...</p>
                        </div>
                    ) : users.length === 0 ? (
                        <div className="p-12 text-center">
                            <UserIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                            <p className="text-gray-500">
                                {searchTerm
                                    ? 'Try adjusting your search or filters'
                                    : 'There are no users in this category yet'
                                }
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Results Count */}
                            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                                <p className="text-sm text-gray-600">
                                    Showing <span className="font-medium">{users.length}</span> users
                                    {searchTerm && ` for "${searchTerm}"`}
                                </p>
                            </div>

                            {/* View Renderer */}
                            {viewMode === 'table' ? <TableView /> : <GridView />}

                            {/* Pagination */}
                            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                                <div className="text-sm text-gray-500">
                                    Page 1 of 1
                                </div>
                                <div className="flex space-x-2">
                                    <button className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50" disabled>
                                        Previous
                                    </button>
                                    <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50" disabled>
                                        Next
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </motion.div>

                {/* Modals */}
                <UserDetailsModal />
                <EditUserModal />
            </div>
        </div>
    );
};

export default UsersManagement;